import React, { useEffect, useRef, useState } from "react";
import { collection, doc, limit, onSnapshot, orderBy, query, updateDoc, where, getDoc } from "firebase/firestore";
import { db } from "../@shared/firebase/config";
import MailToast from "./MailToast";

const MAIL_ICON = require("../@shared/assets/icons/mail_icon.png");

const generalCopy = (docData) => {
  const type = docData?.type || "general";
  const title = docData?.title;
  const body = docData?.body;
  switch (type) {
    case "reward":
      return { message: "New reward!", subtext: title || body || "Claim it now" };
    case "event":
      return { message: "Event starting soon", subtext: title || body || "" };
    case "event_winner":
      return {
        message: "You won an event!",
        subtext: title || body || "Check your campaign rewards",
      };
    case "announcement":
      return { message: "Announcement", subtext: title || body || "" };
    case "gift":
      return { message: "You received a gift", subtext: title || body || "" };
    case "app_release":
      return { message: "Update notes", subtext: title || body || "" };
    case "system":
      return { message: "System update", subtext: title || body || "" };
    default:
      return { message: "You got mail", subtext: title || body || "" };
  }
};

const dmCopy = (threadDoc) => {
  const participants = threadDoc?.participants || [];
  const participantMeta = threadDoc?.participantMeta || {};
  const lastMessage = threadDoc?.lastMessage?.text || "";
  const meId = threadDoc.__meId;
  const friendId = participants.find((x) => x !== meId);
  const friendName = participantMeta?.[friendId]?.name || "Player";
  const mentionedMe = Array.isArray(threadDoc?.lastMessage?.mentionUserIds)
    ? threadDoc.lastMessage.mentionUserIds.includes(meId)
    : false;
  return {
    message: mentionedMe
      ? `${friendName} mentioned you`
      : `New DM from ${friendName}`,
    subtext: lastMessage,
  };
};

export default function MailToastManager({ userId, openMailModal, mailOpen = false }) {
  const [enabled, setEnabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("You got mail");
  const [subtext, setSubtext] = useState("");
  const [imageSrc] = useState(MAIL_ICON);

  const timerRef = useRef();
  const pausedRef = useRef(false);

  const lastGeneralTsRef = useRef(0);
  const lastUnreadSumRef = useRef(0);
  const lastDMTopRef = useRef(null); // for smarter DM copy (top thread)

  // Load pref (default true)
  useEffect(() => {
    if (!userId) return;
    const userRef = doc(db, "users", userId);
    const unsub = onSnapshot(userRef, (snap) => {
      const data = snap.data() || {};
      const next = data?.prefs?.mailToastsEnabled;
      setEnabled(next !== false);
    });
    return () => unsub();
  }, [userId]);

  // Persist toggle
  const handleToggleEnabled = async (next) => {
    setEnabled(next);
    if (!userId) return;
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, { "prefs.mailToastsEnabled": next });
  };

  const startTimer = (ms = 3000) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!pausedRef.current) setOpen(false);
      else startTimer(1000); // check again in 1s while paused
    }, ms);
  };

  const showToast = ({ message, subtext = "" }) => {
    if (!enabled) return;
    setMsg(message);
    setSubtext(subtext);
    setOpen(true);
    startTimer(3500); // a little longer since it's bigger
  };

  // Hover pause hooks
  const onHoverPauseChange = (paused) => {
    pausedRef.current = paused;
    if (!paused) {
      // resume countdown
      startTimer(1500);
    } else {
      clearTimeout(timerRef.current);
    }
  };

  // GENERAL: newest unread, smarter copy
  useEffect(() => {
    if (!userId) return;
    const ref = collection(db, "users", userId, "mail");
    const qGen = query(
      ref,
      where("type", "!=", "dm"),
      orderBy("type"),
      orderBy("createdAt", "desc"),
      limit(1)
    );
    const unsub = onSnapshot(qGen, (snap) => {
      const d = snap.docs[0];
      if (!d) return;
      const data = d.data();
      const createdAt = data?.createdAt?.toMillis?.() ?? 0;
      if (data?.unread && createdAt > lastGeneralTsRef.current) {
        lastGeneralTsRef.current = createdAt;
        const { message, subtext } = generalCopy(data);
        showToast({ message, subtext });
      }
    });
    return () => unsub();
  }, [userId, enabled]);

  // DMs: if total unread increases, toast using the top thread's info
  useEffect(() => {
    if (!userId) return;
    const ref = collection(db, "users", userId, "mail");
    const qDM = query(ref, where("type", "==", "dm"), orderBy("updatedAt", "desc"), limit(5));
    const unsub = onSnapshot(qDM, (snap) => {
      // top threads
      const threads = snap.docs.map((doc) => {
        const d = doc.data();
        d.__meId = userId; // annotate for copy helper
        return { id: doc.id, ...d };
      });
      // total unread
      const total = threads.reduce((sum, t) => sum + Number(t.unreadCount || 0), 0);

      if (total > lastUnreadSumRef.current && !mailOpen) {
        const top = threads[0];
        lastDMTopRef.current = top;
        const { message, subtext } = dmCopy(top || {});
        showToast({ message, subtext });
      }
      lastUnreadSumRef.current = total;
    });
    return () => unsub();
  }, [userId, enabled, mailOpen]);

  return (
    <MailToast
      open={open}
      onClose={() => setOpen(false)}
      enabled={enabled}
      onToggleEnabled={handleToggleEnabled}
      onClick={() => {
        setOpen(false);
        openMailModal?.(); // open your global MailModal
      }}
      onHoverPauseChange={onHoverPauseChange}
      message={msg}
      subtext={subtext}
      imageSrc={imageSrc}
    />
  );
}
